/**
 * Мотивационные реплики после закрытия задачи.
 *
 * Подход подсмотрен у Duolingo: короткая эмоциональная реакция сразу после
 * действия закрепляет поведение лучше, чем накопительная статистика в конце
 * недели. Правила формулировок:
 *
 *  - хвалим за действие, а не за личные качества («сделал», не «ты умный»);
 *  - без канцелярита и без восклицаний через каждое слово;
 *  - формулировки разной длины, чтобы карточка не выглядела шаблоном.
 *
 * Реплики разделены по поводу: обычное закрытие, закрытие всех задач дня,
 * длинная серия. Одинаковая похвала за разное усилие быстро обесценивается.
 */

export interface CelebrationLine {
  title: string;
  subtitle: string;
  emoji: string;
}

/** Обычное закрытие задачи. */
export const TASK_DONE_LINES: CelebrationLine[] = [
  { title: 'Готово', subtitle: 'Минус одна задача из головы', emoji: '✨' },
  { title: 'Так держать', subtitle: 'Ты двигаешься, а это главное', emoji: '🔥' },
  { title: 'Отличный шаг', subtitle: 'Из таких шагов и складывается результат', emoji: '👏' },
  { title: 'Сделано', subtitle: 'Дела любят, когда их доводят до конца', emoji: '✅' },
  { title: 'Красиво', subtitle: 'План превращается в факт', emoji: '💫' },
  { title: 'Ещё одна', subtitle: 'Ты сегодня в хорошем ритме', emoji: '⚡' },
  { title: 'Умница', subtitle: 'Сложное становится привычным', emoji: '🌱' },
  { title: 'Плюс к себе', subtitle: 'Ты держишь слово, данное себе', emoji: '🤝' },
  { title: 'Чисто', subtitle: 'Список стал короче', emoji: '🧹' },
  { title: 'Есть контакт', subtitle: 'Дисциплина сильнее настроения', emoji: '🎯' },
];

/** Закрыты все задачи дня. */
export const DAY_COMPLETE_LINES: CelebrationLine[] = [
  { title: 'День закрыт', subtitle: 'Все задачи выполнены. Это лучший день недели', emoji: '🏆' },
  { title: 'Чистый лист', subtitle: 'Ни одной задачи в остатке — можно выдохнуть', emoji: '🌤️' },
  { title: 'Полный сбор', subtitle: 'Ты сделал всё, что запланировал', emoji: '💎' },
  { title: 'Идеально', subtitle: 'План выполнен на сто процентов', emoji: '🎉' },
];

/**
 * Серия дней подряд.
 *
 * Реплики говорят о регулярности и ни слова — о завершении. Раньше здесь было
 * «Привычка почти сформирована», и эта фраза выпадала и на второй день серии,
 * и в день, когда срок был уже пройден: человек получал поздравление с
 * закреплением привычки и тут же читал, что она сформирована только «почти».
 */
export const STREAK_LINES: CelebrationLine[] = [
  { title: 'Серия растёт', subtitle: 'Ты не пропускаешь — и это заметно', emoji: '🔥' },
  { title: 'Не сбавляешь', subtitle: 'Регулярность важнее интенсивности', emoji: '📈' },
  { title: 'Ритм пойман', subtitle: 'День за днём — и без надрыва', emoji: '🎵' },
  { title: 'Ещё один день', subtitle: 'Ты делаешь это, даже когда не хочется', emoji: '🌱' },
];

/**
 * Привычка дошла до своего срока.
 *
 * Отдельный повод и отдельные слова: это не «ещё один день серии», а конец
 * пути, который человек сам себе назначил. Формулировки говорят о том, что
 * действие перестало требовать усилия, — именно в этом смысл срока, а не в
 * самом числе дней.
 */
export const HABIT_FORMED_LINES: CelebrationLine[] = [
  { title: 'Привычка закреплена', subtitle: 'Срок пройден полностью — теперь это часть тебя', emoji: '🏅' },
  { title: 'Дошли до конца', subtitle: 'Столько дней подряд делают привычку своей', emoji: '🌳' },
  { title: 'Сделано насовсем', subtitle: 'Дальше это уже не требует решимости', emoji: '🧭' },
  { title: 'Есть', subtitle: 'Ты держал слово ровно столько, сколько обещал себе', emoji: '💪' },
];

export type CelebrationKind = 'task' | 'day' | 'streak' | 'habitFormed';

/**
 * Выбирает реплику, стараясь не повторить предыдущую.
 * Повтор подряд разрушает эффект «живой» реакции.
 */
const POOLS: Record<CelebrationKind, CelebrationLine[]> = {
  task: TASK_DONE_LINES,
  day: DAY_COMPLETE_LINES,
  streak: STREAK_LINES,
  habitFormed: HABIT_FORMED_LINES,
};

let lastLine: string | null = null;

export function pickCelebrationLine(kind: CelebrationKind): CelebrationLine {
  const pool = POOLS[kind];

  const candidates = pool.length > 1 ? pool.filter((line) => line.title !== lastLine) : pool;
  const chosen = candidates[Math.floor(Math.random() * candidates.length)] ?? pool[0]!;
  lastLine = chosen.title;
  return chosen;
}
